import { h, Component, ComponentChild } from 'preact'
import Zendesk, { ZendeskData } from './Zendesk'
import AlertComponent, {AlertComponentProps} from '../ui-components/Alert'
import Header from '../header'
import axios, {AxiosResponse, AxiosError} from 'axios'
import errorMessages from './ErrorMessages'

interface IntegrationResponseData {
  apiToken: string,
  subdomain: string,
  agentEmail: string,
  isActive: boolean,
  isFederatedSearchActive: boolean,
  isTicketEscalationActive: boolean,
  locale: string,
  searchableCategories: string[]
}
type IntegrationResponse = AxiosResponse<IntegrationResponseData>

interface SyncResponseData {
  count: number
}

type SyncResponse = AxiosResponse<SyncResponseData>

interface Props {
  errorCallback: (errorMessage: string) => void
  successCallback: (message: string) => void
  alertComponent: ComponentChild
}

interface State extends ZendeskData {}

class ZendeskIntegration extends Component<Props, State> {
  endpoint: string

  constructor () {
    super()
    this.state = {
      apiToken: '',
      subdomain: '',
      agentEmail: '',
      apiTokenError: '',
      subdomainError: '',
      agentEmailError: '',
      showConfirmationMessage: false,
      hasInstalledCredentials: false,
      syncInProgress: false,
      enablingInProgress: false,
      isFederatedSearchActive: false,
      isTicketEscalationActive: false,
      locale: '',
      searchableCategories: []
    }
    this.endpoint = '/integration/zendesk/settings'
    this.onApiTokenChanged = this.onApiTokenChanged.bind(this)
    this.onSubdomainChanged = this.onSubdomainChanged.bind(this)
    this.onAgentEmailChanged = this.onAgentEmailChanged.bind(this)
    this.apiErrorHandler = this.apiErrorHandler.bind(this)
    this.onInstall = this.onInstall.bind(this)
    this.onSync = this.onSync.bind(this)
    this.onDelete = this.onDelete.bind(this)
    this.onShowConfirmationMessage = this.onShowConfirmationMessage.bind(this)
    this.onHideConfirmationMessage = this.onHideConfirmationMessage.bind(this)
    this.toggleZendeskTicketCreation = this.toggleZendeskTicketCreation.bind(this)
    this.toggleFederatedSearch = this.toggleFederatedSearch.bind(this)
    this.onLocaleChanged = this.onLocaleChanged.bind(this)
    this.onSearchableCategoriesChanged = this.onSearchableCategoriesChanged.bind(this)
  }

  apiErrorHandler (error: AxiosError) {
    if (error.response === undefined) {
      this.props.errorCallback(errorMessages.internetConnection)
      return
    }

    this.props.errorCallback(errorMessages.generic)
  }

  componentDidMount () {
    axios.get(this.endpoint)
      .then((res: IntegrationResponse) => {
        const {apiToken, subdomain, agentEmail} = res.data
        this.setState({
          apiToken,
          subdomain,
          agentEmail,
          hasInstalledCredentials: !!(apiToken && subdomain && agentEmail),
          isTicketEscalationActive: res.data.isTicketEscalationActive,
          isFederatedSearchActive: res.data.isFederatedSearchActive,
          locale: res.data.locale,
          searchableCategories: res.data.searchableCategories
        })
      })
      .catch(this.apiErrorHandler)
  }

  toggleZendeskTicketCreation () {
    axios.post('/integration/zendesk/toggleTicketEscalation', {
      isActive: !this.state.isTicketEscalationActive
    })
      .then(() => {
        this.setState({
          isTicketEscalationActive: !this.state.isTicketEscalationActive
        })
      })
      .catch(this.apiErrorHandler)
  }

  toggleFederatedSearch () {
    this.setState({enablingInProgress : true})
    axios.post('/integration/zendesk/toggleFederatedSearch', {
      isActive: !this.state.isFederatedSearchActive,
      locale: this.state.locale,
      searchableCategories: this.state.searchableCategories
    })
      .then((res: SyncResponse) => {
        this.setState({
          isFederatedSearchActive: !this.state.isFederatedSearchActive,
          enablingInProgress: false
        }, () => {
          if (this.state.isFederatedSearchActive) {
            const successMessage = `Federated search is enabled on your community. ${res.data.count} Zendesk articles are successfully synchronized!`
            this.props.successCallback(successMessage)
          }
        })
      })
      .catch(this.apiErrorHandler)
  }

  onApiTokenChanged (e: ChangeEvent) {
    this.setState({ apiToken: e.target.value })
    this.setState({ apiTokenError: '' })
  }

  onSubdomainChanged (e: ChangeEvent) {
    this.setState({ subdomain: e.target.value })
    this.setState({ subdomainError: '' })
  }

  onAgentEmailChanged (e: ChangeEvent) {
    this.setState({ agentEmail: e.target.value })
    this.setState({ agentEmailError: '' })
  }

  onLocaleChanged (e: ChangeEvent) {
    this.setState({ locale: e.target.value })
  }

  onSearchableCategoriesChanged (e: ChangeEvent) {
    this.setState({ searchableCategories: e.target.value.split(',') })
  }

  validateApiToken () {
    this.setState({ apiTokenError: this.state.apiToken ? '' : errorMessages.emptyField })
  }

  validateSubdomain () {
    this.setState({ subdomainError: this.state.subdomain ? '' : errorMessages.emptyField })
  }

  validateEmail () {
    if (!this.state.agentEmail) {
      this.setState({ agentEmailError: errorMessages.emptyField })
    } else if (!this.isValidEmail(this.state.agentEmail)) {
      this.setState({ agentEmailError: errorMessages.invalidEmail })
    } else {
      this.setState({ agentEmailError: '' })
    }
  }

  isValidEmail (email: string) {
    const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return re.test(email)
  }

  maskApiToken (apiToken: string) {
    const tokenLength = apiToken.length
    const unmaskedLength = Math.round(tokenLength / 4)
    const unmaskedPart = apiToken.substr(0, unmaskedLength)

    return unmaskedPart + '*'.repeat(tokenLength - unmaskedLength)
  }

  onInstall () {
    const successMessage = 'Awesome! Your moderation team will now see a \'Create Zendesk Ticket\' option for all questions, conversations, and replies in Control.'
    this.validateApiToken()
    this.validateSubdomain()
    this.validateEmail()

    if (this.state.apiTokenError || this.state.subdomainError || this.state.agentEmailError) {
      return
    }

    axios.post(this.endpoint, this.state)
      .then(() => {
        const apiToken = this.maskApiToken(this.state.apiToken)

        this.setState({ apiToken, hasInstalledCredentials: true })
        this.props.successCallback(successMessage)
      })
      .catch(error => {
        if (error.response === undefined) {
          this.props.errorCallback(errorMessages.internetConnection)
          return
        }

        this.props.errorCallback(errorMessages.generic)
      })
  }

  onSync () {
    
    this.setState({ syncInProgress: true })

    axios.post('/integration/zendesk/sync', [])
      .then((res: SyncResponse) => {
        const successMessage = `${res.data.count} Zendesk articles are successfully synchronized!`
        this.props.successCallback(successMessage)
        this.setState({ syncInProgress: false })
      })
      .catch(error => {
        this.setState({ syncInProgress: false })
        if (error.response === undefined) {
          this.props.errorCallback(errorMessages.internetConnection)
          return
        }

        if (error.response.status === 409) {
          this.props.errorCallback(errorMessages.configurationExists)
          return
        }

        this.props.errorCallback('Sorry, synchronisation failed, please try again.')
      })
  }

  onDelete () {
    axios.delete(this.endpoint)
      .then(() => {
        this.setState({
          apiToken: '',
          subdomain: '',
          agentEmail: '',
          apiTokenError: '',
          subdomainError: '',
          agentEmailError: '',
          hasInstalledCredentials: false,
          locale:'',
          searchableCategories: []
        })
      })
      .catch(this.apiErrorHandler)

    this.onHideConfirmationMessage()
  }

  onShowConfirmationMessage () {
    this.setState({ showConfirmationMessage: true })
  }

  onHideConfirmationMessage () {
    this.setState({ showConfirmationMessage: false })
  }

  render (props: Props) {
    return <div class='integrations-zendesk'>
      <Header title='Integrations' disableCancelButton disableSaveButton />
      { props.alertComponent }
      <Zendesk
        data={this.state}
        onApiTokenChanged={this.onApiTokenChanged}
        onSubdomainChanged={this.onSubdomainChanged}
        onAgentEmailChanged={this.onAgentEmailChanged}
        onInstall={this.onInstall}
        onSync={this.onSync}
        onDelete={this.onDelete}
        onShowConfirmationMessage={this.onShowConfirmationMessage}
        onHideConfirmationMessage={this.onHideConfirmationMessage}
        toggleZendeskTicketCreation={this.toggleZendeskTicketCreation}
        toggleFederatedSearch={this.toggleFederatedSearch}
        onLocaleChanged={this.onLocaleChanged}
        onSearchableCategoriesChanged={this.onSearchableCategoriesChanged}
      />
    </div>
  }
}

export default (props: AlertComponentProps) =>
  <AlertComponent {...props}
    isVisibleFor={5000}
    component={ZendeskIntegration} />
